﻿import React, { useState, useEffect } from 'react';
import { Intent } from '@blueprintjs/core';
import { useSelector } from 'react-redux';
import { UserService } from 'services';
import { NotificationToaster, AlertUnsavedData, AlertConfirm, NotificationInline } from "components/notifications";
import { Button, ButtonSave } from "components/buttons";
import { useDirtyData } from "hooks/useDirtyData";
import { UserBasicSettings } from "components/settings";
import { HelperFunctions } from 'helpers';
import { ShowHide } from 'components/layout';
import _ from 'underscore';

export function UserGeneralSettings(props) {

    const [basicSettingsValid, setBasicSettingsValid] = useState(true);
    const [originalValues, setOriginalValues] = useState({ role: {} });
    const [user, setUser] = useState({
        role: {}
    });
    const [saving, setSaving] = useState(false);
    const [isPending, setIsPending] = useState(false);
    const [togglingEnabled, setTogglingEnabled] = useState(false);
    const [showDisableConfirm, setShowDisableConfirm] = useState(false);
    const [showEnableConfirm, setShowEnableConfirm] = useState(false);

    const loggedInUserId = useSelector(state => state.loggedInUser.userId);

    const isDataDirty = useDirtyData(originalValues, user);

    useEffect(() => {
        setUser(props.user);
        setOriginalValues(_.extend({}, props.user));

        if (props.user != null) {
            setIsPending(props.user.status === "Pending");
        }

    }, [props.user]);

    function onSettingsUpdate(userUpdate) {

        if (userUpdate.pendingEmailAddress !== user.pendingEmailAddress) {
            HelperFunctions.executeIfNotNull(props.onSave, userUpdate);
            setOriginalValues({
                ...originalValues,
                pendingEmailAddress: userUpdate.pendingEmailAddress
            });
        }

        setUser(userUpdate);
    }

    function saveUser() {
        setSaving(true);

        if (!basicSettingsValid) {
            NotificationToaster.show(Intent.WARNING, "Settings not valid. Please check your settings and try again.");
            setSaving(false);
            return;
        }

        UserService.putUserBasicInfo(user, props.editingOwnAccount).then(
            () => {
                setSaving(false);
                setOriginalValues(_.extend({}, user));
                NotificationToaster.show(Intent.SUCCESS, "User updated successfully.");
                HelperFunctions.executeIfNotNull(props.onSave, user);
            },
            (error) => {
                setSaving(false);
                const badRequestCode = 400;
                if (error.response.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, error.response.message);
                } else {
                    NotificationToaster.show(Intent.DANGER, "Could not update the user. Please try again.");
                }
            }
        );
    }

    function onBasicSettingsValidationChange(isValid) {
        setBasicSettingsValid(isValid);
    }

    function requestDisableUser() {
        setShowDisableConfirm(true);
    }

    function requestEnableUser() {
        setShowEnableConfirm(true);
    }

    function cancelToggleEnabled() {
        setShowDisableConfirm(false);
        setShowEnableConfirm(false);
    }

    function toggleUserEnabled(enable) {
        setTogglingEnabled(true);

        UserService.setUserEnabled(user.userId, enable).then(
            () => {
                setTogglingEnabled(false);
                setShowDisableConfirm(false);
                setShowEnableConfirm(false);

                const updatedUser = {
                    ...user,
                    disabledDate: enable ? null : new Date().toISOString()
                };
                setUser(updatedUser);

                setOriginalValues(function (prev) {
                    return { ...prev, disabledDate: updatedUser.disabledDate };
                });

                NotificationToaster.show(
                    Intent.SUCCESS,
                    enable ? "User has been successfully enabled." : "User has been successfully disabled."
                );
                HelperFunctions.executeIfNotNull(props.onSave, updatedUser);
            },
            () => {
                setTogglingEnabled(false);
                setShowDisableConfirm(false);
                setShowEnableConfirm(false);
                NotificationToaster.show(
                    Intent.DANGER,
                    enable ? "Could not enable the user. Please try again." : "Could not disable the user. Please try again."
                );
            }
        );
    }

    function onDisableConfirm() {
        toggleUserEnabled(false);
    }

    function onEnableConfirm() {
        toggleUserEnabled(true);
    }

    return (
        <div className="row">
            <h2>User Details</h2>

            <NotificationInline
                show={props.ssoAccount}
                text="This user's name is handled internally by your IT team and cannot be set through Vision"
                intent="info"
            ></NotificationInline>

            <UserBasicSettings
                user={user}
                loading={props.loading}
                editing={true}
                onSettingsUpdate={onSettingsUpdate}
                saving={saving}
                ssoAccount={props.ssoAccount}
                onValidationUpdate={onBasicSettingsValidationChange}
                pending={isPending}
            />

            <AlertUnsavedData
                isDirty={isDataDirty}>
            </AlertUnsavedData>

            <div className="button-row">
                <ShowHide
                    evaluator={!isPending && !props.ssoAccount}
                    show={(
                        <ButtonSave
                            id="user-role-save"
                            onClick={saveUser}
                            disabled={saving}
                            loading={props.loading}
                        ></ButtonSave>
                    )}
                />

                <ShowHide
                    evaluator={user.disabledDate == null && user.userId != null && user.userId !== loggedInUserId && !isPending}
                    show={(
                        <Button
                            id="user-disable"
                            intent="danger"
                            text="Disable User"
                            icon="disable"
                            onClick={requestDisableUser}
                            disabled={togglingEnabled}
                        ></Button>
                    )}
                />

                <ShowHide
                    evaluator={user.disabledDate != null && user.userId != null && user.userId !== loggedInUserId}
                    show={(
                        <Button
                            id="user-enable"
                            intent="primary"
                            text="Enable User"
                            icon="unlock"
                            onClick={requestEnableUser}
                            disabled={togglingEnabled}
                        ></Button>
                    )}
                />
            </div>

            <AlertConfirm
                title="Disable User"
                isOpen={showDisableConfirm}
                onConfirm={onDisableConfirm}
                onCancel={cancelToggleEnabled}
                confirmButtonIntent="danger"
                confirmButtonText="Disable"
                saving={togglingEnabled}
            >
                <p>This user will be signed out shortly and will not be able to sign in again. Are you sure you want to disable this user?</p>
            </AlertConfirm>

            <AlertConfirm
                title="Enable User"
                isOpen={showEnableConfirm}
                onConfirm={onEnableConfirm}
                onCancel={cancelToggleEnabled}
                confirmButtonIntent="primary"
                confirmButtonText="Enable"
                saving={togglingEnabled}
            >
                <p>This user will be able to sign in again. Are you sure you want to enable this user?</p>
            </AlertConfirm>

        </div>
    );
}